/**
 * Prefetch order: קודם נתונים (מוצרים + קטגוריות) → שמירה → ניווט מיידי.
 * תמונות מוצרים נטענות ברקע אחרי הניווט — לא מחזיקים את המשתמש על מסך ההמתנה.
 */

import { base44 } from "@/api/base44Client";
import {
  savePrefetched,
  loadPrefetched,
  getProxyImageUrl,
  PREFETCH_IMAGE_CACHE,
  isImageCacheAvailable,
  LAST_RECEIPT_QTY_VERSION,
} from "./prefetchStorage";
import { attachSalesStatsToProducts, SALES_STATS_PERIOD_VERSION } from "@/utils/productSalesStats";
import { isSlowOrDataSaverConnection } from "@/lib/connectionQuality";
import { ordersListCacheKey, saveOrdersListCache } from "@/utils/ordersListCache";

export { isSlowOrDataSaverConnection } from "@/lib/connectionQuality";

const BULK_PAGE_SIZE = 1000;
/** רקע: יותר מקביליות; לא חוסם UI */
const BG_IMAGE_CONCURRENCY = 8;

function asEntityList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/**
 * צילום קל לדשבורד הבית — נשמר ב-prefetch כדי שמשימות הבית יופיעו גם ב־3G.
 * כשל חלקי לא מפיל את ה-prefetch.
 */
export async function fetchHomeSnapshot(storeId) {
  if (!storeId) return null;
  const settled = await Promise.allSettled([
    base44.entities.Promotion.list(),
    base44.entities.Order.list({
      view: "summary",
      limit: 200,
      sort: "-createdAt",
    }),
    base44.entities.Order.getOrderedProductIds({ storeId }),
    base44.entities.Supplier.list(),
    base44.entities.MustOrderTask.filter({
      store_id: storeId,
      status: "pending",
    }),
    base44.entities.InventoryCount.filter({
      type: "daily",
      store_id: storeId,
    }),
  ]);

  const pick = (i, map = asEntityList) => {
    const r = settled[i];
    if (r.status !== "fulfilled") return map(null);
    return map(r.value);
  };

  const snapshot = {
    promotions: pick(0),
    orders: pick(1),
    orderedProductIds: (() => {
      const r = settled[2];
      if (r.status !== "fulfilled") return [];
      const ids = r.value?.productIds;
      return Array.isArray(ids) ? ids.map(String) : [];
    })(),
    suppliers: pick(3),
    mustOrderTasks: pick(4),
    dailyInventoryCounts: pick(5),
    fetchedAt: Date.now(),
  };

  // גם למסך הזמנות — כדי שייפתח מיד אחרי «התחל הזמנה»
  if (snapshot.orders.length) {
    saveOrdersListCache(ordersListCacheKey({ storeId }), snapshot.orders);
  }

  return snapshot;
}

/** היסטוריית רכישות לכל המוצרים בסניף — נשמרת ב-prefetch לתצוגה מיידית בכרטיסים */
async function fetchLastPurchasesForStore(storeId) {
  try {
    const res = await base44.entities.Order.getLastPurchasesForStore({ storeId });
    return {
      byProductId:
        res?.byProductId && typeof res.byProductId === "object" ? res.byProductId : {},
      byProductQty:
        res?.byProductQty && typeof res.byProductQty === "object" ? res.byProductQty : {},
    };
  } catch {
    return { byProductId: {}, byProductQty: {} };
  }
}

/** מכירות שבוע/חודש לכל הסניף — מצורף למגוון אחרי lite (שמדלג על salesStats) */
export async function fetchStoreSalesByItemCode(storeId) {
  if (!storeId) return {};
  try {
    const res = await base44.entities.DailySale.getStoreProductStats({ storeId });
    return res?.data?.byItemCode && typeof res.data.byItemCode === "object"
      ? res.data.byItemCode
      : {};
  } catch {
    return {};
  }
}

export async function withStoreSalesStats(products, storeId) {
  if (!products?.length || !storeId) return products || [];
  const byItemCode = await fetchStoreSalesByItemCode(storeId);
  return attachSalesStatsToProducts(products, byItemCode);
}

function sortProductsByCompanyOrderInPlace(productsByCompany) {
  for (const key of Object.keys(productsByCompany)) {
    productsByCompany[key].sort((a, b) => {
      const oa = Number(a.order_in_company ?? a.orderInCompany ?? 0);
      const ob = Number(b.order_in_company ?? b.orderInCompany ?? 0);
      if (oa !== ob) return oa - ob;
      return (a.name || "").localeCompare(b.name || "", "he");
    });
  }
}

async function getImageBlobFromCacheOrFetch(url) {
  if (isImageCacheAvailable()) {
    try {
      const cache = await caches.open(PREFETCH_IMAGE_CACHE);
      const cached = await cache.match(url);
      if (cached) {
        return await cached.blob();
      }
    } catch (_) {}
  }
  const res = await fetch(url);
  if (!res.ok) return null;
  const blob = await res.blob();
  if (isImageCacheAvailable()) {
    try {
      const cache = await caches.open(PREFETCH_IMAGE_CACHE);
      await cache.put(url, new Response(blob, { headers: res.headers }));
    } catch (_) {}
  }
  return blob;
}

function productImageUrl(product) {
  const raw = product?.image_url ?? product?.imageUrl ?? product?.image;
  return getProxyImageUrl(raw, true);
}

/** הורדת כל המוצרים — עמוד ראשון ואז שאר העמודים במקביל */
async function fetchAllStoreProducts(storeId, onProgress) {
  const cacheBust = Date.now();
  const listParams = { limit: BULK_PAGE_SIZE, storeId, prefetch: 1, lite: 1, _nc: cacheBust };

  onProgress({ phase: "products", progress: 0, message: "מוריד מוצרים…" });

  const firstRes = await base44.entities.Product.listForMyStore({ ...listParams, page: 1 });
  const firstData = firstRes?.data || [];
  const totalPages = Math.max(1, firstRes?.pagination?.pages || 1);
  const allProducts = [...firstData];

  if (totalPages > 1) {
    const restPages = [];
    for (let p = 2; p <= totalPages; p++) restPages.push(p);
    onProgress({
      phase: "products",
      progress: 20,
      message: `מוריד מוצרים… ${allProducts.length.toLocaleString("he-IL")} (עמוד 1 מתוך ${totalPages})`,
    });
    // עמודים נוספים בזה אחר זה — מונע עומס ו-timeout במקביל
    for (const page of restPages) {
      const res = await base44.entities.Product.listForMyStore({ ...listParams, page });
      allProducts.push(...(res?.data || []));
      onProgress({
        phase: "products",
        progress: 20 + Math.round((page / totalPages) * 25),
        message: `מוריד מוצרים… ${allProducts.length.toLocaleString("he-IL")} (עמוד ${page} מתוך ${totalPages})`,
      });
    }
  }

  onProgress({
    phase: "products",
    progress: 45,
    message: `מוריד מוצרים… ${allProducts.length.toLocaleString("he-IL")}${totalPages > 1 ? ` (${totalPages} עמודים)` : ""}`,
  });

  return { allProducts, storeInfo: firstRes?.store, totalPages };
}

async function cacheProductImagesInBackground(products) {
  if (!isImageCacheAvailable() || !navigator.onLine || isSlowOrDataSaverConnection()) return;

  const urls = [];
  const seen = new Set();
  for (const product of products || []) {
    const url = productImageUrl(product);
    if (!url || seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
  }
  if (!urls.length) return;

  const cache = await caches.open(PREFETCH_IMAGE_CACHE);
  let index = 0;
  const total = urls.length;
  const concurrency = Math.min(BG_IMAGE_CONCURRENCY, total);

  const worker = async () => {
    while (index < total) {
      const url = urls[index++];
      try {
        const cached = await cache.match(url);
        if (!cached) {
          const res = await fetch(url, { cache: "force-cache" });
          if (res.ok) await cache.put(url, res.clone());
        }
      } catch (_) {}
    }
  };

  await Promise.all(Array.from({ length: concurrency }, worker));
}

/** מפעיל טעינת תמונות ברקע — לא מחכים לסיום */
export function startBackgroundImagePrefetch(products) {
  if (!products?.length) return;
  const run = () => {
    cacheProductImagesInBackground(products).catch(() => {});
  };
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(run, { timeout: 3000 });
  } else {
    setTimeout(run, 500);
  }
}

/** @deprecated — השתמשו ב-startBackgroundImagePrefetch */
export async function runBackgroundImagePrefetch(storeId) {
  if (!storeId) return;
  try {
    const { allProducts } = await fetchAllStoreProducts(storeId, () => {});
    startBackgroundImagePrefetch(allProducts);
  } catch (_) {}
}

/**
 * שלב 1: שואב את כל המוצרים, קטגוריות ותת־קטגוריות, שומר במכשיר — בלי להמתין לתמונות.
 */
export async function runPrefetchOrder(storeId, onProgress) {
  if (!storeId) {
    onProgress({ phase: "done", progress: 0, message: "", error: "חסר מזהה חנות" });
    return;
  }

  try {
    const { allProducts, storeInfo: storeFromApi } = await fetchAllStoreProducts(storeId, onProgress);
    const catalogProducts = allProducts;
    const storeInfo = storeFromApi
      ? { ...storeFromApi, _id: storeFromApi._id || storeFromApi.id || storeId }
      : { _id: storeId };

    onProgress({ phase: "categories", progress: 50, message: "טוען קטגוריות וספקים…" });
    const cacheBust = Date.now();
    const listParams = { _nc: cacheBust };
    const [categories, subcategories, subsubcategories, companies] = await Promise.all([
      base44.entities.Category.list(listParams),
      base44.entities.SubCategory.list(listParams),
      base44.entities.SubSubCategory.list(listParams),
      base44.entities.Company.list(listParams),
    ]);
    const catList = Array.isArray(categories) ? categories : [];
    const subList = Array.isArray(subcategories) ? subcategories : [];
    const subSubList = Array.isArray(subsubcategories) ? subsubcategories : [];
    const companiesList = Array.isArray(companies) ? companies : (companies?.data || []);

    const productsByCompany = {};
    for (const p of catalogProducts) {
      const cid = p.company_id?._id ?? p.company_id;
      if (!cid) continue;
      const key = String(cid);
      if (!productsByCompany[key]) productsByCompany[key] = [];
      productsByCompany[key].push(p);
    }
    sortProductsByCompanyOrderInPlace(productsByCompany);

    onProgress({ phase: "purchases", progress: 65, message: "טוען היסטוריית רכישות…" });
    const [lastPurchases, homeSnapshotFresh] = await Promise.all([
      fetchLastPurchasesForStore(storeId),
      fetchHomeSnapshot(storeId),
    ]);
    const lastPurchaseByProductId = lastPurchases.byProductId;
    const lastReceiptQtyByProductId = lastPurchases.byProductQty;
    // אם הצילום נכשל לגמרי — שומרים את הקודם אם יש
    let homeSnapshot = homeSnapshotFresh;
    if (!homeSnapshot) {
      try {
        const prev = await loadPrefetched(storeId);
        homeSnapshot = prev?.homeSnapshot || null;
      } catch {
        homeSnapshot = null;
      }
    }

    onProgress({ phase: "categories", progress: 75, message: "מוריד לוגואים…" });
    const MAX_LOGO_BASE64 = 80 * 1024;
    const companiesWithLogos = await Promise.all(
      (companiesList || []).map(async (c) => {
        if (!c.logo_url) return { ...c };
        const proxyUrl = getProxyImageUrl(c.logo_url);
        if (!proxyUrl) return { ...c };
        try {
          const blob = await getImageBlobFromCacheOrFetch(proxyUrl);
          if (!blob || blob.size > MAX_LOGO_BASE64) return { ...c };
          const dataUrl = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          return { ...c, logo_data_url: dataUrl };
        } catch {
          return { ...c };
        }
      })
    );

    let chainWithLogo = null;
    const chainId = storeInfo?.chain_id?._id ?? storeInfo?.chain_id;
    if (chainId) {
      try {
        const chains = await base44.entities.Chain.list();
        const chain = Array.isArray(chains) ? chains.find((c) => String(c._id || c.id) === String(chainId)) : null;
        if (chain?.logo_url) {
          const proxyUrl = getProxyImageUrl(chain.logo_url);
          if (proxyUrl) {
            try {
              const blob = await getImageBlobFromCacheOrFetch(proxyUrl);
              if (blob && blob.size <= MAX_LOGO_BASE64) {
                const dataUrl = await new Promise((resolve, reject) => {
                  const r = new FileReader();
                  r.onload = () => resolve(r.result);
                  r.onerror = reject;
                  r.readAsDataURL(blob);
                });
                chainWithLogo = { ...chain, logo_data_url: dataUrl };
              }
            } catch {
              // ignore
            }
          }
          if (!chainWithLogo) chainWithLogo = { ...chain };
        } else if (chain) {
          chainWithLogo = { ...chain };
        }
      } catch (_) {}
    }

    onProgress({ phase: "categories", progress: 88, message: "טוען מכירות…" });
    const productsWithSales = await withStoreSalesStats(catalogProducts, storeId);

    const productsByCompanyWithSales = {};
    for (const p of productsWithSales) {
      const cid = p.company_id?._id ?? p.company_id;
      if (!cid) continue;
      const key = String(cid);
      if (!productsByCompanyWithSales[key]) productsByCompanyWithSales[key] = [];
      productsByCompanyWithSales[key].push(p);
    }
    sortProductsByCompanyOrderInPlace(productsByCompanyWithSales);

    onProgress({ phase: "categories", progress: 92, message: "שומר במכשיר…" });
    await savePrefetched(storeId, {
      products: productsWithSales,
      categories: catList,
      subcategories: subList,
      subsubcategories: subSubList,
      store: storeInfo,
      companies: companiesWithLogos,
      productsByCompany: productsByCompanyWithSales,
      chain: chainWithLogo,
      lastPurchaseByProductId,
      lastReceiptQtyByProductId,
      lastReceiptQtyVersion: LAST_RECEIPT_QTY_VERSION,
      lastPurchasesPrefetched: true,
      salesStatsPrefetched: true,
      salesStatsPeriodVersion: SALES_STATS_PERIOD_VERSION,
      homeSnapshot,
    });

    startBackgroundImagePrefetch(productsWithSales);

    onProgress({
      phase: "done",
      progress: 100,
      message: "מוכן",
    });
  } catch (err) {
    const message = err?.message || "שגיאה לא צפויה";
    onProgress({
      phase: "done",
      progress: 0,
      message: "",
      error: message,
    });
    throw err;
  }
}

const LIGHT_PAGE_SIZE = 1000;

let lightPrefetchInFlight = null;

/** עדכון מגוון "קל" — בלי תמונות */
export async function runLightPrefetch(storeId) {
  if (!storeId) return;
  if (lightPrefetchInFlight) return lightPrefetchInFlight;

  const run = async () => {
    const { allProducts, storeInfo: storeFromApi } = await fetchAllStoreProducts(storeId, () => {});
    const catalogProducts = allProducts;
    const storeInfo = storeFromApi
      ? { ...storeFromApi, _id: storeFromApi._id || storeFromApi.id || storeId }
      : { _id: storeId };

    const [categories, subcategories, subsubcategories, companies] = await Promise.all([
      base44.entities.Category.list(),
      base44.entities.SubCategory.list(),
      base44.entities.SubSubCategory.list(),
      base44.entities.Company.list(),
    ]);
    const catList = Array.isArray(categories) ? categories : [];
    const subList = Array.isArray(subcategories) ? subcategories : [];
    const subSubList = Array.isArray(subsubcategories) ? subsubcategories : [];
    const companiesList = Array.isArray(companies) ? companies : (companies?.data || []);

    const productsByCompany = {};
    for (const p of catalogProducts) {
      const cid = p.company_id?._id ?? p.company_id;
      if (!cid) continue;
      const key = String(cid);
      if (!productsByCompany[key]) productsByCompany[key] = [];
      productsByCompany[key].push(p);
    }
    sortProductsByCompanyOrderInPlace(productsByCompany);

    const [lastPurchases, productsWithSales, homeSnapshotFresh] = await Promise.all([
      fetchLastPurchasesForStore(storeId),
      withStoreSalesStats(catalogProducts, storeId),
      fetchHomeSnapshot(storeId),
    ]);
    const lastPurchaseByProductId = lastPurchases.byProductId;
    const lastReceiptQtyByProductId = lastPurchases.byProductQty;

    let homeSnapshot = homeSnapshotFresh;
    if (!homeSnapshot) {
      try {
        const prev = await loadPrefetched(storeId);
        homeSnapshot = prev?.homeSnapshot || null;
      } catch {
        homeSnapshot = null;
      }
    }

    const productsByCompanyWithSales = {};
    for (const p of productsWithSales) {
      const cid = p.company_id?._id ?? p.company_id;
      if (!cid) continue;
      const key = String(cid);
      if (!productsByCompanyWithSales[key]) productsByCompanyWithSales[key] = [];
      productsByCompanyWithSales[key].push(p);
    }
    sortProductsByCompanyOrderInPlace(productsByCompanyWithSales);

    let chain = null;
    const chainId = storeInfo?.chain_id?._id ?? storeInfo?.chain_id;
    if (chainId) {
      try {
        const chains = await base44.entities.Chain.list();
        chain = Array.isArray(chains) ? chains.find((c) => String(c._id || c.id) === String(chainId)) : null;
      } catch (_) {}
    }

    await savePrefetched(storeId, {
      products: productsWithSales,
      categories: catList,
      subcategories: subList,
      subsubcategories: subSubList,
      store: storeInfo,
      companies: companiesList,
      productsByCompany: productsByCompanyWithSales,
      chain: chain || null,
      lastPurchaseByProductId,
      lastReceiptQtyByProductId,
      lastReceiptQtyVersion: LAST_RECEIPT_QTY_VERSION,
      lastPurchasesPrefetched: true,
      salesStatsPrefetched: true,
      salesStatsPeriodVersion: SALES_STATS_PERIOD_VERSION,
      homeSnapshot,
    });

    startBackgroundImagePrefetch(productsWithSales);
  };

  lightPrefetchInFlight = run().finally(() => {
    lightPrefetchInFlight = null;
  });
  return lightPrefetchInFlight;
}
